import { useMemo } from 'react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { ContentLoader } from '@frontend/design-system';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { prepareAppointmentInfo } from '../../schedule-calendar-components/helpers';
import { ApptPanelContextProvider, ApptPanelContextType } from './appt-panel-context';
import { PanelDetails } from './panel-details';
import { PanelHeader } from './panel-header';
import { useGetPersonInfo } from './use-get-person-info';

export const AppointmentDetailsPanel = () => {
  const { appointmentDetailsEntryPoint, selectedAppointment } = useScheduleAppointmentInfoPanelShallowStore(
    'appointmentDetailsEntryPoint',
    'selectedAppointment'
  );
  const { getLocationTimezoneAbbr } = useLocationTimezone();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const personId = selectedAppointment?.person?.personId ?? '';
  const locationId = selectedAppointment?.locationId ?? '';
  const { personInfo, isLoadingPersonInfo } = useGetPersonInfo(personId, locationId);

  const apptDetails = useMemo(() => {
    const appointmentInfoToPrepare = selectedAppointment ? [selectedAppointment] : [];
    return prepareAppointmentInfo(
      appointmentInfoToPrepare ?? [],
      selectedAppointment?.id ?? '',
      getLocationTimezoneAbbr
    );
  }, [getLocationTimezoneAbbr, selectedAppointment]);

  const contextValue = useMemo<ApptPanelContextType>(() => {
    return {
      appointmentDetailsEntryPoint,
      locationId,
      apptDetails,
      selectedAppointment,
      personInfo,
      isLoadingPersonInfo,
      isV3Enabled: isScheduleV3FlagEnabled,
      isIntegratedOffice: isIntegratedOffice,
      shouldShowOperatory: isIntegratedOffice || (isScheduleV3FlagEnabled && isV3OperatorySupported),
    };
  }, [
    locationId,
    appointmentDetailsEntryPoint,
    apptDetails,
    selectedAppointment,
    personInfo,
    isLoadingPersonInfo,
    isScheduleV3FlagEnabled,
    isIntegratedOffice,
    isV3OperatorySupported,
  ]);

  if (!selectedAppointment?.id) return <ContentLoader show />;

  return (
    <ApptPanelContextProvider value={contextValue}>
      <section css={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <PanelHeader />
        <PanelDetails />
      </section>
    </ApptPanelContextProvider>
  );
};
